import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import {
  getBookings,
  getRequestBookings,
} from "../../../redux/reducers/Properties/PropertiesSlice";
import { AppDispatch, RootState } from "redux/store";
import BookingRow from "components/StayCard/BookingRow";
import { actionStatus, getTabs } from "components/StayCard/BookingViews";
import SectionGridFeaturePlacesSkeleton from "shared/Skelton/SectionGirdSkelton";
import Pagination from "shared/Pagination/Pagination";

const ManageBookings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  const [page, setPage] = useState(1);
  const [bookings, setBookings] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  const id = new URLSearchParams(location.search).get("id");
  const { metaData } = useSelector((state: RootState) => state.property);
  const { currentUser } = useSelector((state: RootState) => state.user);
  const isHost = Boolean(currentUser?.is_host);
  const tabs = getTabs(isHost);

  const fetchBookings = useCallback(
    async (pageNumber = 1, searchQuery = "") => {
      if (!currentUser) return;
      setIsLoading(true);
      try {
        const response = await dispatch(
          isHost
            ? getRequestBookings({ page: pageNumber, query: searchQuery })
            : getBookings({ page: pageNumber, query: searchQuery })
        ).unwrap();
        setBookings(response?.bookings || []);
      } catch (error) {
        console.error("Error fetching bookings:", error);
        setBookings([]);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, isHost, currentUser]
  );

  // Load on mount, when the user or URL changes, and (debounced) when searching.
  useEffect(() => {
    if (!currentUser) return;
    const timer = setTimeout(
      () => {
        setPage(1);
        fetchBookings(1, query);
      },
      query ? 400 : 0
    );
    return () => clearTimeout(timer);
  }, [query, id, currentUser, fetchBookings]);

  // Reset the tab if the role changes (host and guest tabs differ).
  useEffect(() => {
    setActiveTab("all");
  }, [isHost]);

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
    fetchBookings(pageNumber, query);
  };

  const refresh = useCallback(() => fetchBookings(page, query), [
    fetchBookings,
    page,
    query,
  ]);

  // Filter by tab, then put bookings that need the viewer's action first.
  // (Array.sort is stable, so the server's order is kept otherwise.)
  const needsAction = actionStatus(isHost);
  const visible = bookings
    .filter((b) => activeTab === "all" || b?.status === activeTab)
    .sort(
      (a, b) =>
        Number(b?.status === needsAction) - Number(a?.status === needsAction)
    );

  // Counts only cover the loaded page, so hide them when there are more pages.
  const showCounts = !metaData?.total_pages || metaData.total_pages <= 1;
  const countFor = (value: string) =>
    value === "all"
      ? bookings.length
      : bookings.filter((b) => b?.status === value).length;

  const emptyMessage = () => {
    if (query) return `No results for "${query}". Try a different search.`;
    if (activeTab !== "all") return "Nothing in this tab right now.";
    return isHost
      ? "New booking requests for your properties will appear here."
      : "When you request a room, it will appear here.";
  };

  if (isLoading && bookings.length === 0) {
    return (
      <div className="p-10">
        <SectionGridFeaturePlacesSkeleton />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">
              {isHost ? "Manage bookings" : "My reservations"}
            </h1>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              {isHost
                ? "Answer requests, issue invoices, and track payments."
                : "Pay, track, or cancel your requests."}
            </p>
          </div>
          <Link
            to="/my_bookings"
            className="text-sm text-orange-500 hover:text-orange-600 whitespace-nowrap"
          >
            View confirmed bookings &rarr;
          </Link>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 my-5">
          {tabs.map((tab) => {
            const active = activeTab === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                onClick={() => setActiveTab(tab.value)}
                className={`px-4 py-1.5 rounded-full text-sm border transition ${
                  active
                    ? "bg-neutral-900 text-white border-neutral-900 dark:bg-white dark:text-neutral-900 dark:border-white"
                    : "border-neutral-300 dark:border-neutral-600 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                }`}
              >
                {tab.label}
                {showCounts ? ` ${countFor(tab.value)}` : ""}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative mb-5 max-w-sm">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by property name"
            className="w-full pl-3 pr-8 py-2 text-sm border border-neutral-200 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:border-orange-400"
          />
          {query && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setQuery("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 text-lg leading-none"
            >
              ×
            </button>
          )}
        </div>

        {/* List */}
        {visible.length > 0 ? (
          <div className={`space-y-3 ${isLoading ? "opacity-60" : ""}`}>
            {visible.map((booking) => (
              <BookingRow
                key={booking.id}
                booking={booking}
                isHost={isHost}
                onChanged={refresh}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 border border-neutral-200 dark:border-neutral-700 rounded-2xl bg-white dark:bg-neutral-900">
            <p className="text-base font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              No bookings here
            </p>
            <p className="text-sm text-neutral-400">{emptyMessage()}</p>
            {!isHost && activeTab === "all" && !query && (
              <Link
                to="/listing-stay-map"
                className="inline-block mt-4 text-sm text-orange-500 hover:text-orange-600 underline"
              >
                Browse rooms
              </Link>
            )}
          </div>
        )}

        {/* Pagination */}
        {metaData?.total_pages > 1 && (
          <div className="flex justify-center mt-10">
            <Pagination
              currentPage={page}
              totalPages={metaData.total_pages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageBookings;